import { type ChangeEvent, useCallback, useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { Trans } from '@lingui/react/macro';
import { Info } from '@mui/icons-material';
import {
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Button } from '@repo/videa-components/src/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import { Input } from '@repo/videa-components/src/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { Switch } from '@repo/videa-components/src/ui/switch';
import { useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { isEqual } from 'lodash';

import type { PreferenceKey } from '../../../../@types/preferences';
import { preferencesMetaData } from '../../../../@types/preferences';
import { PreferenceButton } from '../../../../common-components/composed/preferenceButton/PreferenceButton';
import { endpoints } from '../../../../react-query/endpoints';
import { usePreferences } from '../../../../react-query/usePreferences';
import { green400, red400 } from '../../../../theme/videa-style-tokens';
import {
  StoreKeys,
  useLocalStorage,
} from '../../../../utils/hooks/useLocalStorage';

const StyledTableCell = styled(TableCell)`
  &.has-text-green {
    color: ${green400};
  }
  &.has-text-red {
    color: ${red400};
  }
`;

export const PreferencesCustomizer = () => {
  const { serverData } = usePreferences();
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [localStorageOverrides, setLocalStorageOverrides] = useLocalStorage<
    Record<string, any>
  >(StoreKeys.PREFERENCES_OVERRIDES_KEY, {});
  const [newOverrides, setNewOverrides] = useState(localStorageOverrides ?? {});

  const displayPrefs = useMemo(
    () => Object.entries(serverData ?? {}).sort(),
    [serverData],
  );

  const filteredPrefs = useMemo(() => {
    if (!searchQuery.trim()) return displayPrefs;
    const q = searchQuery.trim().toLowerCase();
    return displayPrefs.filter(
      ([name, value]) =>
        name.toLowerCase().includes(q) ||
        String(value).toLowerCase().includes(q),
    );
  }, [displayPrefs, searchQuery]);

  const handleOpen = () => setIsOpen(true);

  const handleChangeSelect = useCallback(
    (flagName: string, possibleValuesType?: 'number' | 'string') =>
      (value: string) => {
        if (!newOverrides) return;

        if (value === 'none') {
          // If user selects the "None" option, then flag should be removed from overrides.
          const newValue = { ...newOverrides };
          delete newValue[flagName];
          setNewOverrides(newValue);
        } else if (value === 'true' || value === 'false') {
          const newValue = {
            ...newOverrides,
            [flagName]: value === 'true',
          };
          setNewOverrides(newValue);
        } else if (possibleValuesType) {
          const newValue = {
            ...newOverrides,
            [flagName]:
              possibleValuesType === 'number' ? parseInt(value) : value,
          };
          setNewOverrides(newValue);
        }
      },
    [newOverrides],
  );

  const handleChangeInput = useCallback(
    (flagName: string, originalValue: number | string) =>
      (event: ChangeEvent<HTMLInputElement>) => {
        if (!newOverrides) return;

        if (event.target.value === '') {
          // If user clears the field, then flag should be removed from overrides.
          const value = { ...newOverrides };
          delete value[flagName];
          setNewOverrides(value);
        } else {
          const value = {
            ...newOverrides,
            [flagName]:
              typeof originalValue === 'number'
                ? parseInt(event.target.value)
                : event.target.value,
          };
          setNewOverrides(value);
        }
      },
    [newOverrides, setNewOverrides],
  );

  const getControl = useCallback(
    (flagName: string, flagValue: any) => {
      // `preferencesMetaData` contains preferences that are string (with fixed
      // set of options), number (with fixed set of options), boolean and
      // string, AND boolean and number.
      const preferenceMetaData = preferencesMetaData[flagName as PreferenceKey];
      if (preferenceMetaData) {
        const { type, possibleValues } = preferenceMetaData;
        return (
          <Select
            value={newOverrides?.[flagName]?.toString() ?? 'none'}
            onValueChange={handleChangeSelect(
              flagName,
              typeof possibleValues[0] === 'number' ? 'number' : 'string',
            )}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {type.includes('boolean') && (
                <SelectItem value="true">True</SelectItem>
              )}
              {type.includes('boolean') && (
                <SelectItem value="false">False</SelectItem>
              )}
              {possibleValues
                .filter((value) => value != null && value !== undefined)
                .map((value) => (
                  <SelectItem key={value} value={String(value)}>
                    {String(value)}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        );
      }

      if (typeof flagValue === 'boolean') {
        return (
          <Select
            value={newOverrides?.[flagName]?.toString() ?? 'none'}
            onValueChange={handleChangeSelect(flagName)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select option" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              <SelectItem value="true">True</SelectItem>
              <SelectItem value="false">False</SelectItem>
            </SelectContent>
          </Select>
        );
      } else if (
        typeof flagValue === 'string' ||
        typeof flagValue === 'number'
      ) {
        return (
          <div className="vai-space-y-2">
            <Input
              onChange={handleChangeInput(flagName, flagValue)}
              type={typeof flagValue === 'number' ? 'number' : 'text'}
              value={newOverrides?.[flagName] ?? ''}
              placeholder="Enter value"
            />
            {typeof flagValue === 'string' && (
              <p className="vai-text-xs vai-text-muted-foreground">
                Possible values are unknown. Dev may need to add entry to
                preferencesMetaData.
              </p>
            )}
          </div>
        );
      }
    },
    [handleChangeInput, handleChangeSelect, newOverrides],
  );

  const handleClickClear = useCallback(() => {
    setLocalStorageOverrides({});
    setNewOverrides({});
    queryClient.invalidateQueries([endpoints.preferences]);
  }, [queryClient, setLocalStorageOverrides]);

  const handleClickSave = useCallback(() => {
    setLocalStorageOverrides(newOverrides);
    queryClient.invalidateQueries([endpoints.preferences]);
  }, [newOverrides, queryClient, setLocalStorageOverrides]);

  const shouldAllowSave = !isEqual(newOverrides, localStorageOverrides);
  const shouldAllowClear = !!Object.entries(newOverrides).length;

  const handleTogglePreference = useCallback(
    (preferenceKey: string, currentValue: unknown) => {
      if (!newOverrides) return;

      const newValue = { ...newOverrides };
      const serverValue =
        serverData?.[preferenceKey as keyof typeof serverData];

      if (currentValue) {
        newValue[preferenceKey] = false;
      } else if (serverValue !== undefined) {
        delete newValue[preferenceKey];
      } else {
        newValue[preferenceKey] = true;
      }

      setNewOverrides(newValue);
    },
    [newOverrides, setNewOverrides, serverData],
  );

  const getCurrentValue = useCallback(
    (preferenceKey: string) => {
      if (newOverrides?.[preferenceKey] !== undefined) {
        return newOverrides[preferenceKey];
      }
      return serverData?.[preferenceKey as keyof typeof serverData];
    },
    [newOverrides, serverData],
  );

  return (
    <>
      <PreferenceButton
        className="pref-button"
        onClick={handleOpen}
        startIcon={<Info />}
        data-testid="preferences-customizer-button"
      >
        Preferences Customizer
      </PreferenceButton>

      <Dialog
        open={isOpen}
        onOpenChange={(open) => {
          setIsOpen(open);
          if (!open) {
            setSearchQuery('');
          }
        }}
      >
        <DialogContent
          className="vai-max-w-[900px] vai-w-[90vw] vai-h-[95vh] vai-gap-6 !vai-p-0 !vai-flex vai-flex-col"
          hideClose
        >
          <DialogHeader className="vai-px-6 vai-bg-background vai-z-10 vai-border-b vai-pt-6 vai-pb-4 vai-bg-white !vai-flex !vai-flex-row vai-justify-between vai-items-center">
            <div className="vai-flex-1 vai-flex vai-items-center vai-gap-3">
              <DialogTitle>
                <Trans>Preferences Customizer</Trans>
              </DialogTitle>
              <Input
                type="search"
                placeholder="Search preferences by name or value"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="vai-max-w-72 vai-font-normal"
              />
            </div>
            <div className="vai-flex vai-gap-2">
              <Button
                disabled={!shouldAllowClear}
                onClick={handleClickClear}
                variant="outline"
              >
                Clear All
              </Button>
              <Button disabled={!shouldAllowSave} onClick={handleClickSave}>
                Save
              </Button>
            </div>
          </DialogHeader>

          <div className="vai-flex-1 vai-px-6 vai-pb-6 vai-overflow-y-auto">
            {serverData && (
              <div className="vai-space-y-4 vai-p-4 vai-bg-zinc-100 vai-rounded-lg vai-font-inter">
                <h3 className="vai-text-lg vai-font-semibold vai-text-gray-900">
                  Quick Toggles
                </h3>
                <div className="vai-grid vai-grid-cols-2 vai-gap-2 !vai-mt-2">
                  {serverData.cleanClaims && (
                    <div className="vai-flex vai-items-center vai-justify-between vai-p-3 vai-bg-white vai-rounded-md vai-border">
                      <div>
                        <div className="vai-font-medium vai-text-sm">
                          Insurance Claims
                        </div>
                        <div className="vai-text-xs vai-text-gray-500">
                          Enable/disable insurance claim features
                        </div>
                      </div>
                      <Switch
                        checked={getCurrentValue('cleanClaims')}
                        onCheckedChange={() =>
                          handleTogglePreference(
                            'cleanClaims',
                            getCurrentValue('cleanClaims'),
                          )
                        }
                      />
                    </div>
                  )}

                  {serverData.isVideaNotesEnabled && (
                    <div className="vai-flex vai-items-center vai-justify-between vai-p-3 vai-bg-white vai-rounded-md vai-border">
                      <div>
                        <div className="vai-font-medium vai-text-sm">
                          Videa Notes
                        </div>
                        <div className="vai-text-xs vai-text-gray-500">
                          Enable/disable Videa notes functionality
                        </div>
                      </div>
                      <Switch
                        checked={getCurrentValue('isVideaNotesEnabled')}
                        onCheckedChange={() =>
                          handleTogglePreference(
                            'isVideaNotesEnabled',
                            getCurrentValue('isVideaNotesEnabled'),
                          )
                        }
                      />
                    </div>
                  )}

                  {(serverData.displayDentalPhotos === true ||
                    serverData.displayDentalPhotos === 'on') && (
                    <div className="vai-flex vai-items-center vai-justify-between vai-p-3 vai-bg-white vai-rounded-md vai-border">
                      <div>
                        <div className="vai-font-medium vai-text-sm">
                          Display Dental Photos
                        </div>
                        <div className="vai-text-xs vai-text-gray-500">
                          Show/hide dental photos in the interface
                        </div>
                      </div>
                      <Switch
                        checked={
                          getCurrentValue('displayDentalPhotos') === true ||
                          getCurrentValue('displayDentalPhotos') === 'on'
                        }
                        onCheckedChange={() => {
                          const currentValue = getCurrentValue(
                            'displayDentalPhotos',
                          );
                          const isCurrentlyEnabled =
                            currentValue === true || currentValue === 'on';
                          const updatedOverrides = { ...newOverrides };

                          if (isCurrentlyEnabled) {
                            updatedOverrides.displayDentalPhotos = false;
                          } else {
                            delete updatedOverrides.displayDentalPhotos;
                          }

                          setNewOverrides(updatedOverrides);
                        }}
                      />
                    </div>
                  )}

                  {Array.isArray(serverData.enableInsights) &&
                    serverData.enableInsights.length > 0 && (
                      <div className="vai-flex vai-items-center vai-justify-between vai-p-3 vai-bg-white vai-rounded-md vai-border">
                        <div>
                          <div className="vai-font-medium vai-text-sm">
                            Insights Dashboard
                          </div>
                          <div className="vai-text-xs vai-text-gray-500">
                            Enable/disable insights dashboard features
                          </div>
                        </div>
                        <Switch
                          checked={
                            Array.isArray(getCurrentValue('enableInsights')) &&
                            getCurrentValue('enableInsights').length > 0
                          }
                          onCheckedChange={() => {
                            const currentValue =
                              getCurrentValue('enableInsights');
                            const isCurrentlyEnabled =
                              Array.isArray(currentValue) &&
                              currentValue.length > 0;
                            const updatedOverrides = { ...newOverrides };

                            if (isCurrentlyEnabled) {
                              updatedOverrides.enableInsights = false;
                            } else {
                              delete updatedOverrides.enableInsights;
                            }

                            setNewOverrides(updatedOverrides);
                          }}
                        />
                      </div>
                    )}

                  <div className="vai-flex vai-items-center vai-justify-between vai-p-3 vai-bg-white vai-rounded-md vai-border">
                    <div>
                      <div className="vai-font-medium vai-text-sm">
                        Insurance Summary Modal
                      </div>
                      <div className="vai-text-xs vai-text-gray-500">
                        Enable/disable insurance summary modal
                      </div>
                    </div>
                    <Switch
                      checked={getCurrentValue('insSummaryModal')}
                      onCheckedChange={() =>
                        handleTogglePreference(
                          'insSummaryModal',
                          getCurrentValue('insSummaryModal'),
                        )
                      }
                    />
                  </div>

                  {serverData.isInsuranceEligibilityEnabled &&
                    !serverData.enableVoiceNotesStandalone && (
                      <div className="vai-flex vai-items-center vai-justify-between vai-p-3 vai-bg-white vai-rounded-md vai-border">
                        <div>
                          <div className="vai-font-medium vai-text-sm">
                            AutoVerify
                          </div>
                          <div className="vai-text-xs vai-text-gray-500">
                            Enable/disable AutoVerify feature
                          </div>
                        </div>
                        <Switch
                          checked={getCurrentValue(
                            'isInsuranceEligibilityEnabled',
                          )}
                          onCheckedChange={() =>
                            handleTogglePreference(
                              'isInsuranceEligibilityEnabled',
                              getCurrentValue('isInsuranceEligibilityEnabled'),
                            )
                          }
                        />
                      </div>
                    )}
                </div>
              </div>
            )}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Flag Name</TableCell>
                    <TableCell>Server Value</TableCell>
                    <TableCell>Override Value</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPrefs.map(([name, value]) => {
                    const control = getControl(name, value);
                    if (!control) return null;
                    return (
                      <TableRow key={name}>
                        <TableCell>{name}</TableCell>
                        <StyledTableCell
                          data-testid="flag-server-value"
                          className={classNames({
                            'has-text-green': value === true,
                            'has-text-red': value === false,
                          })}
                        >
                          {value.toString()}
                        </StyledTableCell>
                        <TableCell>{control}</TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredPrefs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3}>No preferences found</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
